/**
 * THERAPIST NOTES ONTOLOGY SERVICE
 *
 * Gestión de la ontología personal del terapeuta: nodos conceptuales,
 * conexiones entre apuntes, y auto-extracción de conceptos.
 * Extraído de therapist-notes.service.ts para mantener responsabilidad única.
 */

import { db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';
import { semanticLayerService } from '@/core/semantic';
import { TherapistOntology, OntologyNode } from '@/core/types/models/therapist/therapist-notes.model';

const logger = Logger.getLogger('TherapistNotesOntologyService');

const COLLECTIONS = {
  ONTOLOGY: 'therapist_ontology',
};

class TherapistNotesOntologyService {

  /**
   * Obtener o crear la ontología personal del terapeuta
   */
  async getOntology(therapistId: string): Promise<TherapistOntology> {
    try {
      const docRef = db.collection(COLLECTIONS.ONTOLOGY).doc(therapistId);
      const doc = await docRef.get();

      if (doc.exists) {
        return { id: doc.id, ...doc.data() } as TherapistOntology;
      }

      const now = new Date().toISOString();
      const newOntology: TherapistOntology = {
        id: therapistId,
        therapistId,
        nodes: [],
        totalNodes: 0,
        totalConnections: 0,
        lastUpdatedAt: now,
        settings: {
          autoExtractConcepts: true,
          showSemanticLayerMapping: true,
          preferredVisualization: 'graph',
        },
        createdAt: now,
        updatedAt: now,
      };

      await docRef.set(newOntology);
      return newOntology;

    } catch (error: any) {
      logger.error('Failed to get ontology', error, { therapistId });
      throw error;
    }
  }

  /**
   * Actualizar ontología a partir de un nuevo apunte
   */
  async updateOntologyFromNote(
    therapistId: string,
    noteId: string,
    analysis: ReturnType<typeof semanticLayerService.analyzeContent>
  ): Promise<void> {
    try {
      const ontology = await this.getOntology(therapistId);

      if (!ontology.settings.autoExtractConcepts) return;

      const now = new Date().toISOString();
      let updated = false;

      for (const detected of analysis.detectedConcepts.slice(0, 5)) {
        const existingNode = ontology.nodes.find(n => n.conceptId === detected.conceptId);

        if (existingNode) {
          existingNode.mentionCount += 1;
          existingNode.lastMentionedAt = now;
          if (!existingNode.derivedFromNoteIds.includes(noteId)) {
            existingNode.derivedFromNoteIds.push(noteId);
          }
          updated = true;
        } else if (detected.confidence > 0.5) {
          const conceptInfo = semanticLayerService.getConceptInfo(detected.conceptId);

          if (conceptInfo) {
            const newNode: OntologyNode = {
              id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              conceptId: detected.conceptId,
              label: conceptInfo.name,
              description: conceptInfo.description,
              relatedNodeIds: [],
              derivedFromNoteIds: [noteId],
              mentionCount: 1,
              lastMentionedAt: now,
            };

            ontology.nodes.push(newNode);
            updated = true;
          }
        }
      }

      if (updated) {
        let connections = 0;
        for (const node of ontology.nodes) {
          node.relatedNodeIds = [];
          for (const otherNode of ontology.nodes) {
            if (node.id === otherNode.id) continue;

            const sharedNotes = node.derivedFromNoteIds.filter(
              nId => otherNode.derivedFromNoteIds.includes(nId)
            );

            if (sharedNotes.length > 0 && !node.relatedNodeIds.includes(otherNode.id)) {
              node.relatedNodeIds.push(otherNode.id);
              connections++;
            }
          }
        }

        ontology.totalNodes = ontology.nodes.length;
        ontology.totalConnections = connections / 2;
        ontology.lastUpdatedAt = now;
        ontology.updatedAt = now;

        await db.collection(COLLECTIONS.ONTOLOGY).doc(therapistId).set(ontology);

        logger.info('Ontology updated', {
          therapistId,
          nodes: ontology.totalNodes,
          connections: ontology.totalConnections
        });
      }

    } catch (error: any) {
      logger.warn('Failed to update ontology', { therapistId, error: error.message });
    }
  }

  /**
   * Agregar nodo manual a la ontología
   */
  async addOntologyNode(therapistId: string, node: Omit<OntologyNode, 'id'>): Promise<OntologyNode> {
    try {
      const ontology = await this.getOntology(therapistId);
      const now = new Date().toISOString();

      const newNode: OntologyNode = {
        ...node,
        id: `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };

      ontology.nodes.push(newNode);
      ontology.totalNodes = ontology.nodes.length;
      ontology.updatedAt = now;
      ontology.lastUpdatedAt = now;

      await db.collection(COLLECTIONS.ONTOLOGY).doc(therapistId).set(ontology);
      return newNode;

    } catch (error: any) {
      logger.error('Failed to add ontology node', error, { therapistId });
      throw error;
    }
  }

  /**
   * Actualizar configuración de la ontología
   */
  async updateOntologySettings(
    therapistId: string,
    settings: Partial<TherapistOntology['settings']>
  ): Promise<void> {
    try {
      const ontology = await this.getOntology(therapistId);

      ontology.settings = { ...ontology.settings, ...settings };
      ontology.updatedAt = new Date().toISOString();

      await db.collection(COLLECTIONS.ONTOLOGY).doc(therapistId).set(ontology);

    } catch (error: any) {
      logger.error('Failed to update ontology settings', error, { therapistId });
      throw error;
    }
  }
}

export const therapistNotesOntologyService = new TherapistNotesOntologyService();
